import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FmPageTitle, FmUnderlineNav, FmTemperatureCard, FmMaterialCard, FmWarningBox, FmBlackPopup } from "../../../Components";
import { useIsMobile } from "../../../Hooks";
import { CalculateUtils, TimeUtils } from "../../../Utils";
import { AgriSolutionState, GeneralState } from "../../../Redux";
import { LOGGED_IN_ROUTES, SOIL_TYPES_KEYS, TEMPERATURE_MAX, TEMPERATURE_MIN, WATER_REGIMES_KEYS } from "../../../Constants";
import { BottomSection } from "../Components";
import useStyles from "./SoilStagePage.styles";
import { FmAllModals } from "../../../Components/Modals";

const SoilStagePage = () => {
    const classes = useStyles();
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const soilState = useSelector(AgriSolutionState.Selectors.selectSoilState);
    const loading = useSelector(AgriSolutionState.Selectors.selectSaveInProgress);
    const { activeMetricMethod } = useSelector(GeneralState.Selectors.selectMetricMethods);
    const [lastKnowActiveMethod, setLastKnownActiveMethod] = useState(activeMetricMethod);

    const { temperatures, selectedSoil, selectedWater, selectedTemperatureUnit } = useSelector(AgriSolutionState.Selectors.selectTemperatureState);

    const [firstMonth, setFirstMonth] = useState<string>("");
    const [lastMonth, setLastMonth] = useState<string>("");
    const [activeWater, setActiveWater] = useState<number>(selectedWater);
    const [activeSoil, setActiveSoil] = useState<number>(selectedSoil);
    const [forceStateInit, setForceStateInit] = useState<number>(0);
    const [months, setMonths] = useState<
        {
            label: string;
            temperature: number;
            date: number;
        }[]
    >([]);

    const [showClearStepModal, setShowClearStepModal] = useState<boolean>(false);
    const [showSaveItemModal, setShowSaveItemModal] = useState<boolean>(false);

    const [hasSaveButton, setHasSaveButton] = useState<boolean>(false);
    const [hasClearButton, setHasClearButton] = useState<boolean>(false);
    const [ShowBlackSnackbar, setShowBlackSnackbar] = useState<boolean>(false);

    const showClearThisStep = useSelector(GeneralState.Selectors.selectShowClearThisStepModal);

    const isUserSave = useSelector(AgriSolutionState.Selectors.selectIsUserSave);

    let startingWater = selectedWater;
    let startingSoil = selectedSoil;
    let startingTemperatures = temperatures;

    useEffect(() => {
        (window as any).dataLayer.push({
            event: "SoilStage",
            pageUrl: "https://multimatch.haifa-group.com/multi-match/agri-solution/soil",
            pageTitle: "SoilStage",
        });
    }, []);

    useEffect(() => {
        const monthsToSave = months.map((month, index) => ({
            date: month.date,
            value: month.temperature,
            index: index,
        }));

        const anyHasValues = !!(
            activeWater !== startingWater ||
            activeSoil !== startingSoil ||
            (!(JSON.stringify(startingTemperatures) === JSON.stringify(monthsToSave)) && monthsToSave.length > 0)
        );

        setHasClearButton(anyHasValues);
        setHasSaveButton(true);
        dispatch(AgriSolutionState.Actions.setSoil(activeSoil));
        dispatch(AgriSolutionState.Actions.setWater(activeWater));
    }, [activeWater, activeSoil, months]);

    const setTempratureState = () => {
        const monthsToSave = months.map((month, index) => ({
            date: month.date,
            value: month.temperature,
            index: index,
        }));
        dispatch(
            AgriSolutionState.Actions.setTemperatureState({
                temperatures: monthsToSave,
                selectedSoil: activeSoil,
                selectedWater: activeWater,
                selectedTemperatureUnit: activeMetricMethod,
            })
        );
    };

    useEffect(() => {
        const { temperatures, selectedSoil, selectedWater, selectedTemperatureUnit } = soilState;
        setActiveWater(selectedWater);
        setActiveSoil(selectedSoil);

        setMonths(
            temperatures.map((temp) => {
                const date = TimeUtils.createDate(temp.date.toString());
                // Zero based
                const month = date.get("month") + 1;
                return {
                    label: t(`months.${month}`),
                    date: month,
                    temperature:
                        activeMetricMethod === selectedTemperatureUnit
                            ? temp.value
                            : activeMetricMethod === "1"
                            ? CalculateUtils.fahrenheitToCelsius(temp.value)
                            : CalculateUtils.celsiusToFahrenheit(temp.value),
                };
            })
        );
        setFirstMonth(temperatures && temperatures.length > 0 ? t(`months.${TimeUtils.createDate(temperatures[0].date.toString()).get("month") + 1}`) : "");
        setLastMonth(
            temperatures && temperatures.length > 0
                ? t(`months.${TimeUtils.createDate(temperatures[temperatures.length - 1].date.toString()).get("month") + 1}`)
                : ""
        );
    }, [t, soilState, forceStateInit]);

    // Reset temperature value on measurement units update
    useEffect(() => {
        if (lastKnowActiveMethod !== activeMetricMethod) {
            const newMonths = months.map((item) => ({
                ...item,
                temperature:
                    activeMetricMethod === "1" ? CalculateUtils.fahrenheitToCelsius(item.temperature) : CalculateUtils.celsiusToFahrenheit(item.temperature),
            }));
            setMonths(newMonths);
            setLastKnownActiveMethod(activeMetricMethod);
            const monthsToSave = newMonths.map((month, index) => ({
                date: month.date,
                value: month.temperature,
                index: index,
            }));
            dispatch(
                AgriSolutionState.Actions.setTemperatureState({
                    temperatures: monthsToSave,
                    selectedSoil: activeSoil,
                    selectedWater: activeWater,
                    selectedTemperatureUnit: activeMetricMethod,
                })
            );
        }
    }, [activeMetricMethod, lastKnowActiveMethod]);
    // Reset temperature value on measurement units update

    const updateMonthsValue = (index: number, newValue: number) => {
        if (newValue <= (TEMPERATURE_MAX as any)[activeMetricMethod] && newValue >= (TEMPERATURE_MIN as any)[activeMetricMethod]) {
            const newMonths = [...months];
            newMonths[index].temperature = newValue;
            setMonths(newMonths);
        }
    };

    const handleClear = () => {
        const newMonths = months.map((item, index) => ({
            date: item.date,
            value: activeMetricMethod === "1" ? 21 : 70,
            index: index,
        }));

        dispatch(
            AgriSolutionState.Actions.setTemperatureState({
                selectedSoil: 1,
                selectedWater: 1,
                selectedTemperatureUnit: lastKnowActiveMethod,
                temperatures: newMonths,
            })
        );
        setForceStateInit((val) => val + 1);
    };
    const calculationSuccess = () => {
        setHasSaveButton(false); // prevent Prompt when we click Next
        history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_THIRD_STEP);
    };
    const handleSubmit = async () => {
        await dispatch(AgriSolutionState.Thunks.saveSoilState(calculationSuccess));
    };
    const handleBack = () => {
        history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FIRST_STEP);
    };
    const saveSolutionHandler = async () => {
        setHasSaveButton(false);
        dispatch(AgriSolutionState.Actions.setIsUserSave(true));
        await dispatch(AgriSolutionState.Thunks.saveProcess());

        const monthsToSave = months.map((month, index) => ({
            date: month.date,
            value: month.temperature,
            index: index,
        }));
        if (isUserSave) {
            setShowBlackSnackbar(true);
        }

        startingWater = activeWater;
        startingSoil = activeSoil;
        startingTemperatures = monthsToSave;
    };
    const closeSnackbarHandler = () => {
        setShowBlackSnackbar(false);
    };
    //------------ Modals ---------------------//
    const closeClearModalHandle = () => {
        setShowClearStepModal(false);
    };
    const openClearModalHandle = () => {
        setShowClearStepModal(true);
    };
    const closeSaveItemModalHandle = () => {
        setShowSaveItemModal(false);
    };
    const openSaveItemModalHandle = () => {
        setShowSaveItemModal(true);
    };
    //-----------------------------------------//
    return (
        <>
            {ShowBlackSnackbar && (
                <div className={classes.snackbar_container}>
                    <FmBlackPopup text={t("saveItemPopup.snackbar")} snackbar={true} onClose={closeSnackbarHandler} />
                </div>
            )}
            <div className={classes.container}>
                <div className={isMobile ? classes.title_container_mobile : classes.title_container}>
                    <FmPageTitle title={t("agriSolution.selectSoilLabel")} />
                    <FmUnderlineNav
                        title={t("saveItemPopup.save")}
                        onClick={
                            hasSaveButton && !isUserSave
                                ? openSaveItemModalHandle
                                : hasSaveButton && isUserSave
                                ? saveSolutionHandler
                                : () => {
                                      //
                                  }
                        }
                        disabled={!hasSaveButton}
                    />
                </div>
                <div className={isMobile ? classes.temperature_titles_container_mobile : classes.temperature_titles_container}>
                    <div className={classes.temp_left_title}>{t("pageStep1.soilTemp")}</div>
                    <div className={classes.temp_right_container}>
                        <div>
                            {firstMonth} - {lastMonth}
                        </div>
                        <FmUnderlineNav bold={true} title={t("pageStep1.change")} onClick={handleBack} />
                    </div>
                </div>
                <div className={classes.tempArray_container}>
                    {months.map((item, index) => (
                        <FmTemperatureCard
                            key={index}
                            month={item.label}
                            temperature={item.temperature}
                            setTemperature={(newValue: number) => {
                                updateMonthsValue(index, newValue);
                                setTempratureState();
                            }}
                        />
                    ))}
                </div>
                <div className={isMobile ? classes.sliders_container_mobile : classes.sliders_container}>
                    <FmMaterialCard
                        title={t("agriSolution.waterRegime.title")}
                        description={t("agriSolution.waterRegime.description")}
                        sliderMarks={WATER_REGIMES_KEYS.map((item) => ({
                            value: item,
                            label: t(`agriSolution.waterRegime.sliderMarks.${item}`),
                        }))}
                        activeMark={activeWater}
                        setActiveMark={(value) => setActiveWater(value)}
                        type='water'
                    />
                    <FmMaterialCard
                        title={t("agriSolution.soilType.title")}
                        description={t("agriSolution.soilType.description")}
                        sliderMarks={SOIL_TYPES_KEYS.map((item) => ({
                            value: item,
                            label: t(`agriSolution.soilType.sliderMarks.${item}`),
                        }))}
                        activeMark={activeSoil}
                        setActiveMark={(value) => setActiveSoil(value)}
                        type='soil'
                    />
                </div>
                <div className={classes.warning_container}>
                    <FmWarningBox icon={"/multi-match/assets/analysis.png"} description={t("agriSolution.soilWarning")} />
                </div>
                <div className={classes.footer}>
                    <BottomSection
                        onClear={showClearThisStep ? openClearModalHandle : handleClear}
                        canSubmit
                        loading={loading}
                        onSubmit={handleSubmit}
                        onBackButton={handleBack}
                    />
                </div>
                <FmAllModals
                    clearModalIsOpen={showClearStepModal}
                    clearModalOnClose={closeClearModalHandle}
                    clearModalAction={handleClear}
                    saveItemModalIsOpen={showSaveItemModal}
                    saveItemModalOnClose={closeSaveItemModalHandle}
                    saveItemModalAction={saveSolutionHandler}
                    SaveProcessPromptWhen={hasSaveButton}
                    SaveProcessPromptMessage={t("saveProcessPopup.subTitle")}
                />
            </div>
        </>
    );
};

export default SoilStagePage;
